import React from "react"
import { View } from "react-native"
import { ActivityIndicator } from "react-native-paper"
import TextM from "@common/components/text/TextM"
import TextButtonM from "@common/components/button/TextButtonM"
import { BadgeM } from "@common/components/badge/BadgeM"
import { theme } from "@common/theme/theme"
import { formatDate } from "@data/utility/Utils"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"

/**
 * Compact section header with icon
 */
export const SectionLabel = ({ icon, label, color = theme.colors.primary }) => (
    <View
        style={{
            flexDirection: "row",
            alignItems: "center",
            marginBottom: 8,
            marginTop: 16,
        }}
    >
        <FontAwesome5
            name={icon}
            size={11}
            color={color}
            style={{ marginRight: 6, opacity: 0.7 }}
        />
        <TextM
            fontWeight="Bold"
            fontSize={11}
            color={color}
            style={{ textTransform: "uppercase", letterSpacing: 0.5 }}
        >
            {label}
        </TextM>
    </View>
)

/**
 * Urgency colors — must match NextAssessmentWidget
 */
const urgencyColors = {
    low: theme.colors.success,
    medium: theme.colors.warning,
    high: theme.colors.warningDark || "#FF9800",
    critical: theme.colors.error,
}

/**
 * Brief profile section: objectives, AI narrative, trend tags
 */
export const BriefProfileSection = ({ profileBrief, profileData }) => {
    if (!profileData) return null

    return (
        <View style={{ width: "100%" }}>
            {/* AI Profile narrative */}
            {profileBrief?.content?.profile_narrative && (
                <>
                    <SectionLabel icon="align-left" label="Synthèse" />
                    <View
                        style={{
                            backgroundColor: theme.colors.primaryLighter,
                            borderRadius: 10,
                            padding: 12,
                            borderLeftWidth: 3,
                            borderLeftColor: theme.colors.primary,
                        }}
                    >
                        <TextM
                            wrap
                            fontSize={13}
                            color={theme.colors.primaryDark}
                            style={{ lineHeight: 22 }}
                        >
                            {profileBrief.content.profile_narrative}
                        </TextM>
                    </View>
                </>
            )}

            {/* Trend tags removed per CTO feedback */}
        </View>
    )
}

/**
 * Brief session section: last session, assessments, starting block, AI narrative
 */
export const BriefSessionSection = ({ sessionBrief, sessionData, isMultiObjective = false, subjectLabel = null }) => {
    if (!sessionData) return null

    const lastSessionLabel = isMultiObjective && subjectLabel
        ? `Dernière séance en ${subjectLabel}`
        : "Dernière séance"

    return (
        <View style={{ width: "100%" }}>
            {/* Last session */}
            {sessionData?.last_session && (
                <>
                    <SectionLabel icon="history" label={lastSessionLabel} />
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "flex-start",
                        }}
                    >
                        <TextM
                            fontSize={12}
                            color={theme.colors.grey}
                            style={{ marginRight: 8 }}
                        >
                            {formatDate(sessionData.last_session.date)}
                        </TextM>
                        {sessionData.last_session.tutor_tags?.length > 0 && (
                            <View
                                style={{
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                    gap: 4,
                                }}
                            >
                                {sessionData.last_session.tutor_tags
                                    .slice(0, 3)
                                    .map((tag, i) => (
                                        <View
                                            key={i}
                                            style={{
                                                backgroundColor:
                                                    tag.type === "positive"
                                                        ? theme.colors
                                                              .successLight
                                                        : theme.colors
                                                              .warningLight,
                                                borderRadius: 8,
                                                paddingHorizontal: 6,
                                                paddingVertical: 1,
                                            }}
                                        >
                                            <TextM
                                                fontSize={10}
                                                color={
                                                    tag.type === "positive"
                                                        ? theme.colors.success
                                                        : theme.colors.warning
                                                }
                                            >
                                                {tag.label}
                                            </TextM>
                                        </View>
                                    ))}
                            </View>
                        )}
                    </View>
                    {sessionData.last_session.tutor_comment && (
                        <TextM
                            wrap
                            fontSize={13}
                            color={theme.colors.primaryDark}
                            style={{
                                marginTop: 4,
                                fontStyle: "italic",
                            }}
                        >
                            "{sessionData.last_session.tutor_comment}"
                        </TextM>
                    )}
                </>
            )}

            {/* Assessments */}
            {sessionData?.assessments?.length > 0 && (
                <>
                    <SectionLabel
                        icon="calendar-alt"
                        label="Contrôles à venir"
                    />
                    {sessionData.assessments.map((a, i) => {
                        const color = urgencyColors[a.urgency] || theme.colors.grey
                        const titlePart = a.title || a.type || "Contrôle"
                        const displayTitle = a.subject ? `${titlePart} — ${a.subject}` : titlePart
                        const displayDate = a.date_label || formatDate(a.date)
                        return (
                            <View
                                key={i}
                                style={{
                                    flexDirection: "row",
                                    alignItems: "flex-start",
                                    marginBottom: 6,
                                    backgroundColor: theme.colors.white,
                                    borderRadius: 10,
                                    borderWidth: 1,
                                    borderColor: theme.colors.lightMore,
                                    padding: 12,
                                }}
                            >
                                <FontAwesome5
                                    name="calendar-check"
                                    size={16}
                                    color={color}
                                    style={{ marginRight: 10, marginTop: 2 }}
                                />
                                <View style={{ flex: 1, marginRight: 8 }}>
                                    <TextM
                                        wrap
                                        fontSize={13}
                                        fontWeight="SemiBold"
                                        color={theme.colors.primaryDark}
                                    >
                                        {displayTitle}
                                    </TextM>
                                    <TextM
                                        wrap
                                        fontSize={12}
                                        color={theme.colors.grey}
                                        style={{ marginTop: 2 }}
                                    >
                                        {displayDate}
                                        {a.chapters?.length > 0
                                            ? ` · ${a.chapters.join(", ")}`
                                            : ""}
                                    </TextM>
                                </View>
                                {a.days_until != null && (
                                    <View
                                        style={{
                                            backgroundColor: color,
                                            paddingHorizontal: 8,
                                            paddingVertical: 3,
                                            borderRadius: 10,
                                        }}
                                    >
                                        <TextM
                                            fontSize={11}
                                            fontWeight="Bold"
                                            color={theme.colors.white}
                                        >
                                            J-{a.days_until}
                                        </TextM>
                                    </View>
                                )}
                            </View>
                        )
                    })}
                </>
            )}

            {/* Starting block — student's declared need via Sam */}
            {sessionData?.starting_block && (
                <>
                    <SectionLabel icon="comment-dots" label="L'élève a dit à Sam" />
                    <View
                        style={{
                            backgroundColor: theme.colors.infoLight,
                            borderRadius: 8,
                            padding: 10,
                            borderLeftWidth: 3,
                            borderLeftColor: theme.colors.info,
                        }}
                    >
                        {sessionData.starting_block.chapitre &&
                            sessionData.starting_block.subject_name && (
                                <TextM
                                    wrap
                                    fontSize={13}
                                    fontWeight="Medium"
                                    color={theme.colors.primaryDark}
                                >
                                    Veut travailler :{" "}
                                    {sessionData.starting_block.chapitre} en{" "}
                                    {sessionData.starting_block.subject_name}
                                </TextM>
                            )}
                        {sessionData.starting_block.assessment_declared && (
                            <TextM
                                wrap
                                fontSize={12}
                                color={theme.colors.warning}
                                fontWeight="Medium"
                            >
                                A déclaré un contrôle
                                {sessionData.starting_block.assessment_date
                                    ? ` le ${formatDate(sessionData.starting_block.assessment_date)}`
                                    : ""}
                            </TextM>
                        )}
                        {sessionData.starting_block.summary && (
                            <TextM
                                wrap
                                fontSize={12}
                                color={theme.colors.grey}
                                style={{ marginTop: 2 }}
                            >
                                {sessionData.starting_block.summary}
                            </TextM>
                        )}
                    </View>
                </>
            )}

            {/* AI Session narrative */}
            {sessionBrief?.content?.session_narrative && (
                <>
                    <SectionLabel icon="bullseye" label="Focus séance" />
                    <View
                        style={{
                            backgroundColor: theme.colors.primaryLighter,
                            borderRadius: 10,
                            padding: 12,
                            borderLeftWidth: 3,
                            borderLeftColor: theme.colors.primary,
                        }}
                    >
                        <TextM
                            wrap
                            fontSize={13}
                            color={theme.colors.primaryDark}
                            style={{ lineHeight: 22 }}
                        >
                            {sessionBrief.content.session_narrative}
                        </TextM>
                    </View>
                </>
            )}
        </View>
    )
}

/**
 * Regenerate brief button
 */
export const RegenerateButton = ({ isRegenerating, onRegenerate }) => (
    <View
        style={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            marginTop: 20,
            opacity: isRegenerating ? 0.5 : 1,
        }}
    >
        {isRegenerating ? (
            <ActivityIndicator
                size={12}
                color={theme.colors.light}
                style={{ marginRight: 6 }}
            />
        ) : (
            <FontAwesome5
                name="sync-alt"
                size={11}
                color={theme.colors.light}
                style={{ marginRight: 6 }}
            />
        )}
        <TextButtonM
            label="Régénérer le brief"
            onPress={onRegenerate}
            color={theme.colors.light}
            fontWeight="Medium"
            loading={isRegenerating}
            style={{ fontSize: 12 }}
        />
    </View>
)
